// The round's order, as one button that says what it is doing. The old three-way switch (Top,
// For you, Latest) read as three tabs with no telling them apart (USER 2026-11-03); each choice
// now carries the line that says what it does. The panel is drawn on the body, so no glass
// ancestor can trap it.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownUp, Check, ChevronDown } from "lucide-react";
import type { Lens } from "./lens";
import "./lensmenu.css";

const CHOICES: { id: Lens; name: string; line: (tuned: boolean) => string }[] = [
  { id: "top", name: "Top stories", line: () => "The stories that matter most today, first" },
  {
    id: "foryou",
    name: "For you",
    line: (tuned) => (tuned ? "Topics you follow, save and like, first" : "Follow a topic or save a story and it leads here"),
  },
  { id: "latest", name: "Newest first", line: () => "Every story by the time it was reported" },
];

export function LensMenu({ lens, onLens, tuned, compact = false }: { lens: Lens; onLens: (lens: Lens) => void; tuned: boolean; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const current = CHOICES.find((c) => c.id === lens) ?? CHOICES[0];

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const place = () => {
      const r = btn.current!.getBoundingClientRect();
      setAt({ top: Math.round(r.bottom + 8), right: Math.max(12, Math.round(window.innerWidth - r.right)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || btn.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btn.current?.focus(); } };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`lens-pick${compact ? " compact" : ""}${lens !== "top" ? " is-set" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Order: ${current.name}. Change the order`}
        onClick={() => setOpen((o) => !o)}
      >
        <ArrowDownUp size={compact ? 18 : 14} strokeWidth={1.75} aria-hidden="true" />
        {compact ? null : <span>{current.name}</span>}
        {compact ? null : <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" className="lens-pick-chev" />}
      </button>
      {open && at
        ? createPortal(
            <div ref={panel} className="lens-menu" role="menu" aria-label="Order the round" style={{ top: at.top, right: at.right }}>
              <p className="lens-menu-head">Order the round</p>
              {CHOICES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={lens === c.id}
                  className={`lens-menu-row${lens === c.id ? " on" : ""}`}
                  onClick={() => { onLens(c.id); setOpen(false); }}
                >
                  <span className="lens-menu-copy">
                    <b>{c.name}</b>
                    <small>{c.line(tuned)}</small>
                  </span>
                  {lens === c.id ? <Check size={16} strokeWidth={2} aria-hidden="true" /> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
